import type {
  StaffChatMessage,
  StaffFormRequest,
  StaffPendingAction
} from "./use-staff-chat-stream"

/**
 * Typed-block renderer core (plan slice 3): folds a live `block` SSE event
 * into the assistant message, replacing the legacy tool_result card
 * assembly when the typed_blocks flag is on. Pure so it's unit-testable.
 *
 * v1 semantics:
 * - result: completes the oldest still-running tool chip of that name
 *   (retries produce repeat names) — the block protocol has no start
 *   event, so tool_call keeps opening chips under both paths.
 * - approval/form: append cards.
 * - error: sets the message error (stream-level error event still closes
 *   the stream itself).
 * - text blocks and unknown types/versions are ignored: streaming text
 *   arrives via text deltas; forward compatibility means never throwing.
 */
export function applyBlockToMessage(
  message: StaffChatMessage,
  block: any
): StaffChatMessage {
  if (!block || block.v !== 1) return message
  switch (block.type) {
    case "result": {
      const toolEvents = [...message.toolEvents]
      const idx = toolEvents.findIndex(t => t.running && t.name === block.tool)
      if (idx >= 0) {
        toolEvents[idx] = {
          name: block.tool,
          running: false,
          ok: block.ok,
          rowCount: block.rowCount
        }
      }
      return { ...message, toolEvents }
    }
    case "approval":
      return block.action
        ? {
            ...message,
            pendingActions: [
              ...message.pendingActions,
              block.action as StaffPendingAction
            ]
          }
        : message
    case "form":
      return block.form
        ? {
            ...message,
            formRequests: [
              ...message.formRequests,
              block.form as StaffFormRequest
            ]
          }
        : message
    case "error":
      return { ...message, error: block.message || "The agent hit an error" }
    default:
      return message
  }
}
